/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import _forEach from 'lodash/forEach';
import utilityService from './utility.service';
import d3 from '../globalexports/d3.export';
import AppConst from '../constants/app.constant';
import DefConst from '../constants/def.constant';
import ChartConst from '../constants/chart.constant';
import ConfigDeviceSettings from '../config/device.config';
import searchHistoryService from './searchHistory.service';
import * as AltitudeOptionsConst from '../constants/altitude.constant';
import { processWeatherValue } from './chartData/processWeatherValue';
import { getTempLabelValue } from './chartData/getTempLabelValue';

const bnChartDataProcessService = function () {
  const contents = {};

  function parseYValue(yValue) {
    // ParseFloat only if not null to handle NAN issue
    return (yValue !== null && yValue !== undefined && !Number.isNaN(yValue))
      ? parseFloat(yValue)
      : null;
  }
  contents.parseYValueInterface = function (yValue) {
    return parseYValue(yValue);
  };

  contents.appendWeatherStripData = function (item, objectKey) {
    return processWeatherValue(item, objectKey);
  };

  contents.getAxis = function (
    width,
    height,
    paddingWidth,
    timeZone,
    useLogarithmicScaleForCeiling,
    yAxisRightRectWidth,
  ) {
    return {
      // TBD : can't use AppConst.timeZones.local.key here
      x: timeZone === 'L'
        ? d3.scaleTime().range([(paddingWidth + 4), width - (yAxisRightRectWidth || paddingWidth
          + 4)]).unknown((paddingWidth + 4))
        : d3.scaleUtc().range([(paddingWidth + 4), width - (yAxisRightRectWidth || paddingWidth
          + 4)]).unknown((paddingWidth + 4)),
      y1: useLogarithmicScaleForCeiling
        ? d3.scaleLog().range([height, 0]).unknown(height)
        : d3.scaleLinear().range([height, 0]).unknown(height),
      y2: d3.scaleLinear().range([height, 0]).unknown(height),
    };
  };

  contents.getHighLowOfTemp = function (graphdata, settings) {
    const data = [];
    let day;
    const mapObject = [];
    let highLowObj;
    _forEach(graphdata, (item) => {
      if (settings.timeZone === AppConst.timeZones.zulu.key) {
        day = item.forecast_ts_utc.getDate();
      } else {
        day = item.forecast_ts_local.getDate();
      }
      if (!mapObject[day]) {
        mapObject[day] = {
          values: [],
          childs: [],
        };
      }
      if (item.tmp_display_value) {
        // Push value only if temperature is provided
        mapObject[day].values.push(item.tmp_display_value);
        mapObject[day].childs.push(item);
      }
    });
    mapObject.forEach((item) => {
      // Pushing min temperature of the day
      highLowObj = item.childs[item.values.indexOf(Math.min.apply(Object, item.values))];
      if (highLowObj) {
        data.push(highLowObj);
      }
      // Pushing max temperature of the day
      // Pick the high that comes LATEST in the day: BNFOR-977 that's why need to reverse
      item.childs.reverse();
      item.values.reverse();
      highLowObj = item.childs[item.values.indexOf(Math.max.apply(Object, item.values))];
      if (highLowObj) {
        data.push(highLowObj);
      }
    });
    return data;
  };

  function checkIfAtleastOneNonNullValue(obj, key, chartitem) {
    if (!obj.addToReturnObject) {
      obj.addToReturnObject = !!(chartitem[key] !== null);
    }
  }
  contents.getTmpRlhChartData = function (graphdata, xKey) {
    const chartdata = [];
    const temperatureSeries = utilityService.clone(ChartConst.ChartLabelSpecs.tmp);
    const rlhSeries = utilityService.clone(ChartConst.ChartLabelSpecs.rlh);
    temperatureSeries.values = [];
    rlhSeries.values = [];
    _forEach(graphdata, (chartitem) => {
      checkIfAtleastOneNonNullValue(temperatureSeries, 'tmp_display_value', chartitem);
      checkIfAtleastOneNonNullValue(rlhSeries, 'rlh', chartitem);
      temperatureSeries.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.tmp_display_value),
        tmp_celsius: chartitem.tmp_celsius,
        tmp_fahrenheit: chartitem.tmp_fahrenheit,
        y1: true,
      });
      rlhSeries.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.rlh),
      });
    });
    if (rlhSeries.addToReturnObject || temperatureSeries.addToReturnObject) {
      chartdata.push(temperatureSeries);
      chartdata.push(rlhSeries);
    }
    // Sorting chartdata by sequence in from app.constant
    chartdata.sort((a, b) => parseFloat(a.sequence) - parseFloat(b.sequence));
    return chartdata;
  };

  contents.getCigVisChartData = function (graphdata, xKey, settings, considerElevation) {
    const chartdata = [];
    const cigSeries = utilityService.clone(ChartConst.ChartLabelSpecs.cig);
    const visSeries = utilityService.clone(ChartConst.ChartLabelSpecs.vis);
    let elevation = 0;
    if (settings.airportDetails) {
      elevation = considerElevation
        ? settings.airportDetails.el
        : 0;
    }
    cigSeries.values = [];
    visSeries.values = [];

    function getCeilingValue(chartitem, addElevation) {
      if (chartitem && chartitem.airportDetails) {
        // When it will from route profile then it will use el from chartItem
        // for meteogram it will be empty
        elevation = chartitem.airportDetails.el;
      }
      return Math.min(settings.maximumAltitudeAllowed, chartitem.cig_mapped + (addElevation
        ? elevation
        : 0));
    }

    function getCeilingTooltip(chartitem) {
      if (settings.maximumAltitudeAllowed === AltitudeOptionsConst.MSL6K.feet && chartitem
        .cig_mapped > AltitudeOptionsConst.MSL6K.feet) {
        return settings.maximumAltitudeAllowed;
      }
      return chartitem.cig_mapped;
    }
    _forEach(graphdata, (chartitem) => {
      checkIfAtleastOneNonNullValue(cigSeries, 'cig_mapped', chartitem);
      checkIfAtleastOneNonNullValue(visSeries, 'vis_mapped', chartitem);
      const itemToPush = {
        x: chartitem[xKey],
        y: parseYValue((chartitem.cig_mapped !== null && chartitem.cig_mapped
          !== undefined)
          ? getCeilingValue(chartitem, true)
          : chartitem.cig_mapped),
        cig_mapped: parseYValue(chartitem.cig_mapped),
        cig_real: parseYValue(chartitem.cig),
        tooltipValue: parseYValue((chartitem.cig_mapped !== null && chartitem
          .cig_mapped !== undefined)
          ? utilityService.roundCeilings(getCeilingTooltip(chartitem, false))
          : chartitem.cig_mapped),
        cld: chartitem.cld,
      };
      if (itemToPush.tooltipValue === null) {
        // if ceiling is empty then use no data to show tooltip
        itemToPush.tooltipValue = 'No Data';
      }
      cigSeries.values.push(itemToPush);
      visSeries.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.vis_mapped),
        v: parseYValue(chartitem.cig_mapped) + elevation,
      });
    });
    // if (cigSeries.addToReturnObject || visSeries.addToReturnObject) {
    //     chartdata.push(cigSeries);
    //     chartdata.push(visSeries);
    // }
    chartdata.push(cigSeries);
    chartdata.push(visSeries);
    chartdata.sort((a, b) => parseFloat(a.sequence) - parseFloat(b.sequence));
    return chartdata;
  };

  contents.getWdrWspXwdChartData = function (graphdata, xKey) {
    const chartdata = [];
    // var wdrSeries = utilityService.clone(ChartConst.ChartLabelSpecs.wdr);
    const wspSeries = utilityService.clone(ChartConst.ChartLabelSpecs.wsp);
    const xwdSeries = utilityService.clone(ChartConst.ChartLabelSpecs.xwd);
    const wgsSeries = utilityService.clone(ChartConst.ChartLabelSpecs.wgs);
    // wdrSeries.values = [];
    wspSeries.values = [];
    xwdSeries.values = [];
    wgsSeries.values = [];
    _forEach(graphdata, (chartitem) => {
      // checkIfAtleastOneNonNullValue(wdrSeries, "wdr", chartitem);
      checkIfAtleastOneNonNullValue(wspSeries, 'wsp', chartitem);
      checkIfAtleastOneNonNullValue(xwdSeries, 'xwd', chartitem);
      checkIfAtleastOneNonNullValue(wgsSeries, 'wgs', chartitem);
      // wdrSeries.values.push({
      //     "x": chartitem[xKey],
      //     "y": parseYValue(chartitem.wdr)
      // });
      wspSeries.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.wsp),
        z: parseYValue(chartitem.wdr) || 0, // z storing rotaion value for WSP
      });
      xwdSeries.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.xwd),
      });
      wgsSeries.values.push({
        x: chartitem[xKey],
        l: chartitem.isUsingLamp,
        y: parseYValue(chartitem.wgs) || null, // treat no value as 0 for WGS
      });
    });
    /* Removing WDR from chart
           chartdata.push(wdrSeries);
        */
    if (wspSeries.addToReturnObject) {
      chartdata.push(wspSeries);
    }
    if (wspSeries.addToReturnObject || wgsSeries.addToReturnObject) {
      chartdata.push(wgsSeries);
    }
    if (wspSeries.addToReturnObject || xwdSeries.addToReturnObject) {
      chartdata.push(xwdSeries);
    }
    // Sorting chartdata by sequence in from app.constant
    chartdata.sort((a, b) => parseFloat(a.sequence) - parseFloat(b.sequence));
    return chartdata;
  };

  function setIfToBeReturnedInChartData(obj, key, chartitem) {
    if (!obj.addToReturnObject) {
      obj.addToReturnObject = !!(chartitem[key] > obj.minimumForDisplay);
    }
  }

  contents.getP06Q06t06st6ChartData = function (graphdata, xKey) {
    const chartdata = [];
    const p06Series = utilityService.clone(ChartConst.ChartLabelSpecs.p06);
    const q06Series = utilityService.clone(ChartConst.ChartLabelSpecs.q06);
    const t06Series = utilityService.clone(ChartConst.ChartLabelSpecs.t06);
    const t01Series = utilityService.clone(ChartConst.ChartLabelSpecs.t01);
    const st6Series = utilityService.clone(ChartConst.ChartLabelSpecs.st6);
    const ppoSeries = utilityService.clone(ChartConst.ChartLabelSpecs.ppo);
    const snoSeries = utilityService.clone(ChartConst.ChartLabelSpecs.sno);
    const fzpSeries = utilityService.clone(ChartConst.ChartLabelSpecs.fzp);
    p06Series.values = [];
    q06Series.values = [];
    t06Series.values = [];
    t01Series.values = [];
    st6Series.values = [];
    ppoSeries.values = [];
    snoSeries.values = [];
    fzpSeries.values = [];
    _forEach(graphdata, (chartitem) => {
      // calling by reference to set if series will be added to chart data obj
      setIfToBeReturnedInChartData(p06Series, 'p06', chartitem);
      setIfToBeReturnedInChartData(ppoSeries, 'ppo', chartitem);
      setIfToBeReturnedInChartData(q06Series, 'q06', chartitem);
      setIfToBeReturnedInChartData(t06Series, 't06', chartitem);
      setIfToBeReturnedInChartData(t01Series, 't01', chartitem);
      setIfToBeReturnedInChartData(st6Series, 'st6', chartitem);
      setIfToBeReturnedInChartData(snoSeries, 'sno', chartitem);
      setIfToBeReturnedInChartData(fzpSeries, 'fzp', chartitem);
      p06Series.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.p06),
        isUsingLamp: chartitem.isUsingLamp,
      });
      ppoSeries.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.ppo),
      });
      q06Series.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.q06_mapped),
      });
      t06Series.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.t06),
        isUsingLamp: chartitem.isUsingLamp,
      });
      t01Series.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.t01),
      });
      st6Series.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.st6),
      });
      snoSeries.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.sno),
      });
      fzpSeries.values.push({
        x: chartitem[xKey],
        y: parseYValue(chartitem.fzp),
      });
    });

    if (ppoSeries.addToReturnObject) {
      chartdata.push(ppoSeries);
    }
    if (p06Series.addToReturnObject) {
      chartdata.push(p06Series);
    }
    if (t06Series.addToReturnObject) {
      chartdata.push(t06Series);
    }
    if (t01Series.addToReturnObject) {
      chartdata.push(t01Series);
    }
    // removing q06 from app charts for now:BNFOR-1194
    // if (q06Series.addToReturnObject) {
    //     chartdata.push(q06Series);
    // }
    if (st6Series.addToReturnObject) {
      chartdata.push(st6Series);
    }
    if (snoSeries.addToReturnObject) {
      chartdata.push(snoSeries);
    }
    if (fzpSeries.addToReturnObject) {
      chartdata.push(fzpSeries);
    }
    // Sorting chartdata by sequence in from app.constant
    chartdata.sort((a, b) => parseFloat(a.sequence) - parseFloat(b.sequence));
    return chartdata;
  };

  contents.parseDatesInMode = function (data, mode) {
    if (data) {
      _forEach(data, (item) => {
        if (mode === ChartConst.dateMode.utc) {
          item.day = item.forecast_ts_utc.getDate();
          item.forecast_ts = item.forecast_ts_utc;
        } else if (mode === ChartConst.dateMode.local) {
          item.day = item.forecast_ts_local.getDate();
          item.forecast_ts = item.forecast_ts_local;
        }
      });
    }
    return data;
  };

  contents.processCeilingRangeData = function (ceilingRangeData) {
    const newValue = [];
    function pushNewValue(x, y, insertAt) {
      newValue.push({
        x,
        y,
        insertAt,
      });
    }
    if (ceilingRangeData[0]) {
      _forEach(ceilingRangeData[0].values, (item, key) => {
        let midpoint;
        if (item.y !== ChartConst.lastCigValue) {
          // Left
          if (key > 0 && ceilingRangeData[0].values[key - 1].y === null) {
            midpoint = new Date((ceilingRangeData[0].values[key - 1].x.getTime() + item.x
              .getTime()) / 2);
            pushNewValue(midpoint, item.y, key);
          }
          // Right
          if ((key < ceilingRangeData[0].values.length - 1) && ceilingRangeData[0].values[
            key + 1].y === ChartConst.lastCigValue) {
            midpoint = new Date((ceilingRangeData[0].values[key + 1].x.getTime() + item.x
              .getTime()) / 2);
            pushNewValue(midpoint, item.y, key + 1);
          }
        }
        item.y = (item.y === ChartConst.lastCigValue
          ? null
          : item.y);
      });
    }
    _forEach(newValue, (item, key) => {
      // Push new values into ceiling chart data
      ceilingRangeData[0].values.splice(item.insertAt + key, 0, item);
    });
    return ceilingRangeData;
  };

  contents.filterAloftToGetFreezingLineRecord = function (chartRefData, aloftData) {
    const freezingLineData = {
      tmpDataLowerBound: [],
      tmpDataUpperBound: [],
    };
    let i = 0;
    let j = 0;
    while (i < chartRefData.values.length) {
      j = 0;
      while (j < aloftData.aloftTmpDataLowerBound.length) {
        if (aloftData.aloftTmpDataLowerBound[j].x.getTime() === chartRefData.values[i].x
          .getTime()) {
          freezingLineData.tmpDataLowerBound.push(aloftData.aloftTmpDataLowerBound[j]);
          freezingLineData.tmpDataUpperBound.push(aloftData.aloftTmpDataUpperBound[j]);
        }
        j += 1;
      }
      if (!freezingLineData.tmpDataLowerBound.length || freezingLineData.tmpDataLowerBound
        .length - 1 < i) {
        freezingLineData.tmpDataLowerBound.push({
          x: chartRefData.values[i].x,
          y: null,
        });
        freezingLineData.tmpDataUpperBound.push({
          x: chartRefData.values[i].x,
          y: null,
        });
      }
      i += 1;
    }
    return freezingLineData;
  };

  contents.filterAloftToGetGustLineRecord = function (chartRefData, aloftData, fullData) {
    const gustLineData = [];
    const exactMatchData = [];
    let i = 0;
    let j;
    let yValue;
    while (i < chartRefData.values.length) {
      j = 0;
      while (j < aloftData.viewRelevantData.length) {
        if (aloftData.viewRelevantData[j].model_ts.getTime() === chartRefData.values[i].x
          .getTime()) {
          yValue = null;
          if (aloftData.viewRelevantData[j].altitudes && aloftData.viewRelevantData[j]
            .altitudes[0]) {
            // if gust not present then consider it as null
            yValue = aloftData.viewRelevantData[j].altitudes[0].gust || null;
          }

          exactMatchData.push({
            x: chartRefData.values[i].x,
            altitudes: aloftData.viewRelevantData[j].altitudes,
          });
          gustLineData.push({
            x: chartRefData.values[i].x,
            y: yValue,
          });
        }
        j += 1;
      }
      if (!gustLineData.length || gustLineData.length - 1 < i) {
        gustLineData.push({
          x: chartRefData.values[i].x,
          y: null,
        });
        exactMatchData.push({
          x: chartRefData.values[i].x,
          altitudes: [],
        });
      }
      i += 1;
    }
    i = 0;
    if (fullData.lamp && fullData.lamp.forecast) {
      while (i < gustLineData.length) {
        j = 0;
        while (j < fullData.lamp.forecast.length) {
          if ((gustLineData[i].x - fullData.lamp.forecast[j].forecast_ts_local) === 0) {
            gustLineData[i].y = fullData.lamp.forecast[j].wgs;
          }
          j += 1;
        }
        i += 1;
      }
    }
    return {
      gustLineData,
      exactMatchData,
    };
  };

  function getTooltipPrefix(d, dMain) {
    let tooltipPrefix = `${d.tooltip.prefix}: `;
    if (dMain.isUsingLamp === true) {
      // in mav for first 24hrs
      // p06 is treated as ppo of LAMP and
      // t06 is treated as t01 of LAMP
      if (d.key === ChartConst.ChartLabelSpecs.p06.key) {
        tooltipPrefix = `${ChartConst.ChartLabelSpecs.ppo.tooltip.prefix}: `;
      }
      if (d.key === ChartConst.ChartLabelSpecs.t06.key) {
        tooltipPrefix = `${ChartConst.ChartLabelSpecs.t01.tooltip.prefix}: `;
      }
    }
    return tooltipPrefix;
  }

  function getTooltipValueUnit(seriesKey, d, dMain) {
    let tooltipValueUnit = '';
    // Appending tooltipvalueUnit
    switch (seriesKey) {
      case ChartConst.ChartLabelSpecs.wsp.key:
      case ChartConst.ChartLabelSpecs.wgs.key:
        tooltipValueUnit += `${(dMain.y > 0)
          ? d.tooltip.unit
          : ''} `;
        break;
      default:
        tooltipValueUnit += `${(dMain.y !== null)
          ? d.tooltip.unit
          : ''} `;
    }
    return tooltipValueUnit;
  }

  function getTooltipValue(seriesKey, dMain, d, toWhatDecimal, settings) {
    let tooltipValue = '';
    const versionToUse = ConfigDeviceSettings.settings.apiVersionKey;
    switch (seriesKey) {
      case ChartConst.ChartLabelSpecs.cig.key:
      case ChartConst.ChartLabelSpecs.cigTmp.key:
        if (seriesKey === ChartConst.ChartLabelSpecs.cig.key) {
          if (dMain.y && dMain.y >= Math.min(settings.maximumAltitudeAllowed, ChartConst
            .lastCigValue)) {
            tooltipValue += ChartConst.greaterThan;
          } else if (dMain.y && dMain.y <= (settings.dataMode === 'meteogram'
            ? (ChartConst.firstCigValue + settings.airportDetails.el)
            : ChartConst.firstCigValue)) {
            tooltipValue += ChartConst.lessThan;
          }
        } else if (seriesKey === ChartConst.ChartLabelSpecs.cigTmp.key) {
          if (dMain.y && dMain.y >= settings.maximumAltitudeAllowed) {
            tooltipValue += ChartConst.greaterThan;
          } else if (dMain.y && dMain.y <= ChartConst.firstCigValue) {
            tooltipValue += ChartConst.lessThan;
          }
        }

        if (dMain.y !== null && dMain.y <= (settings.dataMode === 'meteogram'
          ? (ChartConst.firstCigValue + settings.airportDetails.el)
          : dMain.el)) {
          // If we are showing SFC then no need to show <   > sign
          tooltipValue = tooltipValue.replace('<', '');
          tooltipValue = tooltipValue.replace('>', '');
          tooltipValue += ChartConst.cigMinThresholdTooltip;
        } else if (dMain.y !== null && d.key === ChartConst.ChartLabelSpecs.cig.key) {
          if (dMain.y > settings.maximumAltitudeAllowed) {
            tooltipValue += settings.maximumAltitudeAllowed.toFixed(toWhatDecimal);
          } else {
            tooltipValue += dMain.tooltipValue.toFixed(toWhatDecimal);
          }
        } else if (dMain.y !== null) {
          tooltipValue += dMain.y.toFixed(toWhatDecimal);
        } else {
          tooltipValue += ChartConst.GraphConfig.noDataText;
        }
        break;
      case ChartConst.ChartLabelSpecs.wsp.key:
        if (dMain.y === 0) {
          tooltipValue += ChartConst.calmWind;
        } else if (dMain.y > 0 && dMain.z === 0) {
          tooltipValue += ChartConst.variableWind;
          tooltipValue += (dMain.y !== null)
            ? dMain.y.toFixed(toWhatDecimal)
            : ChartConst.GraphConfig.noDataText;
        } else {
          tooltipValue += dMain.z
            ? `${(`00${dMain.z * 10}`).slice(-3) + ChartConst.ChartLabelSpecs.wdr.tooltip.unit} `
            : '';
          tooltipValue += (dMain.y !== null)
            ? dMain.y.toFixed(toWhatDecimal)
            : ChartConst.GraphConfig.noDataText;
        }
        break;
      case ChartConst.ChartLabelSpecs.vis.key:
        if (versionToUse === 3) {
          tooltipValue += (dMain.y !== null)
            ? dMain.y.toFixed(toWhatDecimal)
            : ChartConst.GraphConfig.noDataText;
        } else if (dMain.y === ChartConst.lastVis) {
          tooltipValue += ChartConst.greaterThan + ChartConst.visMaxThresholdTooltip;
        } else if (dMain.y === ChartConst.firstVis) {
          tooltipValue += ChartConst.lessThan + ChartConst.visMinThresholdTooltip;
        } else {
          tooltipValue += (dMain.y !== null)
            ? dMain.y.toFixed(toWhatDecimal)
            : ChartConst.GraphConfig.noDataText;
        }
        break;
      case ChartConst.ChartLabelSpecs.p06.key:
      case ChartConst.ChartLabelSpecs.ppo.key:
      case ChartConst.ChartLabelSpecs.t06.key:
      case ChartConst.ChartLabelSpecs.t01.key:
      case ChartConst.ChartLabelSpecs.st6.key:
      case ChartConst.ChartLabelSpecs.sno.key:
      case ChartConst.ChartLabelSpecs.cp2.key:
      case ChartConst.ChartLabelSpecs.fzp.key:
        tooltipValue += (dMain.y !== null)
          ? (dMain.y < (seriesKey === ChartConst.ChartLabelSpecs.st6.key
            ? ChartConst.st6MinThresholdTooltip
            : ChartConst.precipMinThresholdTooltip)
            ? 'None'
            : dMain.y.toFixed(toWhatDecimal))
          : ChartConst.GraphConfig.noDataText;
        break;
      case ChartConst.ChartLabelSpecs.tmp.key: {
        if (dMain.y !== null) {
          tooltipValue += getTempLabelValue(
            settings.temperatureUnit,
            dMain.tmp_celsius,
            dMain.tmp_fahrenheit,
          )
        } else {
          tooltipValue += ChartConst.GraphConfig.noDataText;
        }
        break;
      }
      default:
        tooltipValue += (dMain.y !== null)
          ? dMain.y.toFixed(toWhatDecimal)
          : ChartConst.GraphConfig.noDataText;
    }
    if (tooltipValue.indexOf(ChartConst.noneTooltip) === -1 && tooltipValue !== ChartConst
      .cigMinThresholdTooltip && seriesKey !== ChartConst.ChartLabelSpecs.tmp.key) {
      tooltipValue += getTooltipValueUnit(seriesKey, d, dMain);
    }
    return tooltipValue;
  }

  contents.formatTooltipText = function (d, dMain, settings) {
    let formattedText = getTooltipPrefix(d, dMain);
    const toWhatDecimal = (d.key === ChartConst.ChartLabelSpecs.q06.key
      ? 1
      : 0);
    if (dMain.tooltip_text) {
      formattedText += dMain.tooltip_text;
    } else {
      formattedText += getTooltipValue(d.key, dMain, d, toWhatDecimal, settings);
      if (dMain.y && (d.key === ChartConst.ChartLabelSpecs.cig.key) && dMain.cld && (dMain
        .cld !== DefConst.clouds.broken && dMain.cld !== DefConst.clouds.overcast)) {
        formattedText = `${d.tooltip.prefix}: ${ChartConst.unlimitedTooltip}`;
      }
    }
    return utilityService.styleTooltip(formattedText, true);
  };

  contents.addHistoryForAirport = function (airportStart) {
    searchHistoryService.setAirportHistory(airportStart);
  };

  contents.getPercentageTicksText = function (d, unitObj, showAbbreviatedYTicks) {
    const tickValue = d;
    return showAbbreviatedYTicks
      ? (d < 100
        ? (tickValue + unitObj.unit)
        : tickValue)
      : (tickValue + unitObj.unit);
  };

  contents.checkIfAllValueNotAtCeiling = function (
    aloftTmpDataUpperBound,
    aloftTmpDataLowerBound,
    settings,
  ) {
    let haveAnyValueLessThanMax = false;
    _forEach(aloftTmpDataUpperBound, (item, key) => {
      if ((item.y !== null && item.y <= settings.maximumAltitudeAllowed) || (
        aloftTmpDataLowerBound[key].y !== null && aloftTmpDataLowerBound[key].y
        <= settings.maximumAltitudeAllowed)) {
        haveAnyValueLessThanMax = true;
      }
    });
    return haveAnyValueLessThanMax;
  };

  contents.normalizeSecondaryRecord = function (
    aloftTmpDataUpperBound,
    aloftTmpDataLowerBound,
    settings,
  ) {
    const indexToSetYasNull = [];
    _forEach(aloftTmpDataUpperBound, (item, key) => {
      if (item.y === null) {
        item.hide = true;
      }
      if (item.y === aloftTmpDataLowerBound[key].y || (settings
        ? item.y < (settings.airportDetails.el + DefConst.aloftCloudIndexMap[0])
        : false)) {
        item.hide = true;
        // Below logic is to make sure that lowerbound and upperbound
        // line is overlapped to make it dark
        if (key === 0) {
          if (aloftTmpDataUpperBound[key + 1].y === aloftTmpDataLowerBound[key + 1].y) {
            indexToSetYasNull.push(key);
          }
        } else if (key === aloftTmpDataUpperBound.length - 1) {
          if (aloftTmpDataUpperBound[key - 1].y === aloftTmpDataLowerBound[key - 1].y) {
            indexToSetYasNull.push(key);
          }
        } else if ((aloftTmpDataUpperBound[key - 1].y === aloftTmpDataLowerBound[key - 1]
          .y) && (aloftTmpDataUpperBound[key + 1].y === aloftTmpDataLowerBound[key + 1]
          .y)) {
          indexToSetYasNull.push(key);
        }
      }
    });
    _forEach(indexToSetYasNull, (item) => {
      // Loop through all index to set its y value null
      aloftTmpDataUpperBound[item].y = null;
      aloftTmpDataUpperBound[item].hide = true;
    });
    return aloftTmpDataUpperBound;
  };

  contents.getAirmetTooltipPrefix = function (airmetPrimary) {
    let html = '';
    const tooltipPrefix = airmetPrimary.hazard_type_identifier;
    if (airmetPrimary.isSigmet && airmetPrimary.hazard_type_identifier !== ChartConst
      .gairmetConfig.convectiveAirmet.refKey) {
      html += `SEV ${tooltipPrefix}`;
    } else if (airmetPrimary.isSigmet && airmetPrimary.hazard_type_identifier === ChartConst
      .gairmetConfig.convectiveAirmet.refKey) {
      html += `${tooltipPrefix.substring(0, 4)} ${airmetPrimary.airsigmet_type}`;
    } else {
      html += tooltipPrefix;
    }
    return html;
  };

  contents.getAirmetTrendlineObject = function (airmetData, refObject, nmXAxis, xAxis, settings) {
    const mainMapping = [];
    let mappedObj;
    let airObj;
    let i;
    let j;
    let elevationOffset;
    // console.log(settings.airportDetails.el);
    _forEach(airmetData, (airmetItem) => {
      mappedObj = utilityService.clone(refObject);
      // Merging properites of refObject and airmetObject
      Object.assign(mappedObj, airmetItem);
      mappedObj.key = airmetItem.hazard_type;
      mappedObj.secondary = true;
      mappedObj.tooltip.prefix = contents.getAirmetTooltipPrefix(mappedObj);
      mappedObj.color = airmetItem.hazardConfig.strokeColor;
      mappedObj.areaColor = airmetItem.hazardConfig.strokeColor;
      mainMapping.push(mappedObj);
    });
    i = 0;
    while (i < mainMapping.length) {
      airObj = mainMapping[i];
      j = 0;
      while (j < airObj.values.length) {
        delete airObj.values[j].cld;
        // console.log(airObj);
        // if (airObj.hazard_type_identifier === "IFR")
        // airObj.values[j].hide = true;
        elevationOffset = 0;
        if (!nmXAxis) {
          if (airObj.isAglAirmet) {
            elevationOffset = (settings.airportDetails.el / 2);
          }
          // for meteogram
          if (airObj.values[j].x >= airObj[airObj.x1Key] && airObj.values[j].x <= airObj[airObj
            .x2Key]) {
            airObj.values[j].y = elevationOffset + airObj.altitude_min + ((airObj.altitude_max
              - airObj.altitude_min) / 2);
            airObj.values[j].tooltip_text = airObj.altitude_tooltip_text;
          } else {
            airObj.values[j].y = null;
            airObj.values[j].hide = true;
          }
        } else if (parseInt(xAxis(airObj.values[j].x).toFixed(0), 10) >= parseInt(airObj
          .left_edge
          .toFixed(0), 10)
          && parseInt(xAxis(airObj.values[j].x).toFixed(0), 10) <= (parseInt(airObj.right_edge
            .toFixed(
              0,
            ), 10) + 1)) {
          airObj.values[j].y = airObj.altitude_min + ((airObj.altitude_max - airObj
            .altitude_min) / 2);
          airObj.values[j].tooltip_text = airObj.altitude_tooltip_text;
        } else {
          airObj.values[j].y = null;
          airObj.values[j].hide = true;
        }
        j += 1;
      }
      i += 1;
    }
    return mainMapping;
  };

  contents.getGairmetColor = function (gairmet) {
    let gairmetConfig;
    switch (gairmet.hazard_type) {
      case ChartConst.gairmetConfig.ceilingVis.key:
        gairmetConfig = utilityService.clone(ChartConst.gairmetConfig.ceilingVis);
        break;
      case ChartConst.gairmetConfig.turbHigh.key:
        gairmetConfig = utilityService.clone(ChartConst.gairmetConfig.turbHigh);
        break;
      case ChartConst.gairmetConfig.turbLow.key:
        gairmetConfig = utilityService.clone(ChartConst.gairmetConfig.turbLow);
        break;
      case ChartConst.gairmetConfig.turb.key:
        gairmetConfig = utilityService.clone(ChartConst.gairmetConfig.turb);
        break;
      case ChartConst.gairmetConfig.icing.key:
        gairmetConfig = utilityService.clone(ChartConst.gairmetConfig.icing);
        break;
      case ChartConst.gairmetConfig.mtnObscn.key:
      case ChartConst.gairmetConfig.mtnObscn.key2:
        gairmetConfig = utilityService.clone(ChartConst.gairmetConfig.mtnObscn);
        break;
      case ChartConst.gairmetConfig.llWindShear.key:
        gairmetConfig = utilityService.clone(ChartConst.gairmetConfig.llWindShear);
        break;
      case ChartConst.gairmetConfig.sfcWinds.key:
      case ChartConst.gairmetConfig.sfcWinds.key2:
        gairmetConfig = utilityService.clone(ChartConst.gairmetConfig.sfcWinds);
        break;
      case ChartConst.gairmetConfig.frzgLvl.key:
      case ChartConst.gairmetConfig.frzgLvl.key2:
      case ChartConst.gairmetConfig.frzgLvl.key3:
        gairmetConfig = utilityService.clone(ChartConst.gairmetConfig.frzgLvl);
        break;
      case ChartConst.gairmetConfig.convectiveAirmet.key:
        gairmetConfig = utilityService.clone(ChartConst.gairmetConfig.convectiveAirmet);
        // If airsigmet_type=SIGMET then red color otherwise YELLOW
        // Yellow for AIRMET/OUTLOOK
        gairmetConfig.strokeColor = (gairmet.airsigmet_type === ChartConst.sigmet_type
          ? gairmetConfig.strokeColorSigmet
          : gairmetConfig.strokeColorAirmet);
        gairmetConfig.priority = (gairmet.airsigmet_type === ChartConst.sigmet_type
          ? gairmetConfig.prioritySigmet
          : gairmetConfig.priorityAirmet);
        break;
      default:
    }
    if (!gairmetConfig) {
      gairmetConfig = {};
      gairmetConfig.strokeColor = '#000000';
    }
    // All  SIGMET will be shown as RED

    if (gairmet.airsigmet_type === ChartConst.sigmet_type || (gairmet.properties && gairmet
      .properties.airSigmetType === ChartConst.sigmet_type)) {
      gairmetConfig.strokeColor = ChartConst.sigmetColor;
    }
    return gairmetConfig;
  };
  contents.getSeriesByKey = function (data, seriesKey) {
    let seriesData;
    _forEach(data, (obj) => {
      if (obj.key === seriesKey) {
        seriesData = obj;
      }
    });
    return seriesData;
  };
  contents.getMetsForMap = function (fullDataObj) {
    switch (AppConst.metServiceProvider) {
      case AppConst.possibleMetServiceProvider.bnforecast:
        if (!fullDataObj.airsigmet) {
          fullDataObj.airsigmet = [];
        }
        if (!fullDataObj.gairmet) {
          fullDataObj.gairmet = [];
        }
        fullDataObj.aviationMet = fullDataObj.airsigmet.concat(fullDataObj.gairmet);
        break;
      case AppConst.possibleMetServiceProvider.bnforecastAllMets:
        if (fullDataObj.bnforecastAllMets) {
          if (!fullDataObj.bnforecastAllMets.airsigmet) {
            fullDataObj.bnforecastAllMets.airsigmet = [];
          }
          if (!fullDataObj.bnforecastAllMets.gairmet) {
            fullDataObj.bnforecastAllMets.gairmet = [];
          }
          fullDataObj.aviationMet = fullDataObj.bnforecastAllMets.airsigmet.concat(fullDataObj
            .bnforecastAllMets.gairmet);
        }
        break;
      case AppConst.possibleMetServiceProvider.aviationmet:
        // If metServiceProvider is aviationmet then dont do
        // anything because right values are avaialble in fullDataObj.aviationmet
        break;
      default:
    }
  };
  contents.getMetsForLayer = function () {
    const metLayersSelector = [];
    const suppressedMets = utilityService.getSuppressedMetFromLocalStorage();
    const { enableRadarFeature } = ConfigDeviceSettings.settings;
    _forEach(ChartConst.typesOfMetsForMapLayers, (value) => {
      const tmp = {
        name: value,
        mets: true,
        checked: true,
      };
      if (suppressedMets.indexOf(value) > -1) {
        tmp.checked = false;
      }
      metLayersSelector.push(tmp);
    });
    _forEach(ChartConst.rulesetLayerMap, (value) => {
      const tmp = {
        name: value,
        ruleset: true,
        checked: true,
      };
      if (suppressedMets.indexOf(value) > -1) {
        tmp.checked = false;
      }
      metLayersSelector.push(tmp);
    });
    if (enableRadarFeature) {
      _forEach(ChartConst.radarLayerMap, (value) => {
        const tmp = {
          name: value,
          radar: true,
          checked: true,
        };
        if (suppressedMets.indexOf(value) > -1) {
          tmp.checked = false;
        }
        metLayersSelector.push(tmp);
      });
    }
    return metLayersSelector;
  };
  contents.setSuppressedMetFromCharts = function (mets) {
    const suppressedMetsLs = utilityService.getSuppressedMetFromLocalStorage();
    _forEach(mets, (value) => {
      if (!value.checked && suppressedMetsLs.indexOf(value.name) < 0) {
        suppressedMetsLs.push(value.name);
      }
      if (value.checked && suppressedMetsLs.indexOf(value.name) > -1) {
        suppressedMetsLs.splice(suppressedMetsLs.indexOf(value.name), 1);
      }
    });
    utilityService.setSuppressedMetLocalStorage(suppressedMetsLs);
  };
  return contents;
};
export default bnChartDataProcessService();
